import React, { useState, useEffect } from 'react';
import Header from '../Header';
import { letterheadAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { notify, confirm } from '../../utils/notify';
import { generateLetterheadPDF } from '../../utils/letterheadPDF';
import { useListControls, ListToolbar, Pagination } from '../../components/ListControls';
import Topbar from "../../components/Topbar";

const statusConfig = {
  APPROVED:  { label:'Approved',           cls:'status-approved', icon:'bi-check-circle' },
  REJECTED:  { label:'Rejected',           cls:'status-rejected', icon:'bi-x-circle' },
  UNDER_RM:  { label:'Under Review (RM)',  cls:'status-pending',  icon:'bi-hourglass-split' },
  UNDER_HOD: { label:'Under Review (HOD)', cls:'status-inreview', icon:'bi-person-check' },
  DRAFT:     { label:'Draft',              cls:'status-hold',     icon:'bi-pencil' },
  SUBMITTED: { label:'Submitted',          cls:'status-pending',  icon:'bi-send' },
};

function ApprovalTimeline({ status }) {
  const steps = ['Submitted', 'Reporting Mgr', 'HOD Review', 'Final Approved'];
  const order = { SUBMITTED:0, UNDER_RM:1, UNDER_HOD:2, APPROVED:3 };
  const cur = status === 'REJECTED' ? -1 : (order[status] ?? 0);
  return (
    <div className="d-flex align-items-center gap-0 mt-2">
      {steps.map((s, i) => (
        <div key={i} className="d-flex align-items-center" style={{flex:1}}>
          <div className="d-flex flex-column align-items-center" style={{flexShrink:0}}>
            <div className="appr-step-dot" style={{background: cur >= i ? (cur === i && status !== 'APPROVED' ? '#4f6ef7' : '#22c55e') : '#f3f4f6', color: cur >= i ? '#fff' : '#aaa'}}>
              {cur > i ? <i className="bi bi-check" style={{fontSize:'0.598rem'}}/> : i + 1}
            </div>
            <div style={{fontSize:'0.478rem',color:cur>=i?'#374151':'#aaa',textAlign:'center',marginTop:2,width:37}}>{s}</div>
          </div>
          {i < 3 && <div style={{flex:1,height:2,background:cur>i?'#22c55e':'#e5e7eb',margin:'0 1px',marginBottom:8}}/>}
        </div>
      ))}
    </div>
  );
}

function LetterheadApprovals() {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('ALL');
  const [acting, setActing] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const d = await letterheadAPI.listRequests({ limit: 100 }); setRequests(d.requests || []); }
    catch (e) { notify.error(e.message || 'Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const approve = async (r) => {
    const res = await confirm.action(`Approve request from ${r.employeeName || 'this employee'}?`, 'This will move it to the next approval level.', 'Yes, Approve', '#16a34a');
    if (!res.isConfirmed) return;
    setActing(r._id);
    try { await letterheadAPI.approve(r._id, 'Approved'); notify.success('Request approved successfully'); await load(); }
    catch (e) { notify.error(e.message || 'Approval failed'); }
    finally { setActing(null); }
  };

  const reject = async (r) => {
    const res = await confirm.reject(`Reject request from ${r.employeeName || 'this employee'}?`);
    if (!res.isConfirmed) return;
    setActing(r._id);
    try { await letterheadAPI.reject(r._id, res.value); notify.success('Request rejected'); await load(); }
    catch (e) { notify.error(e.message || 'Failed'); }
    finally { setActing(null); }
  };

  const downloadPDF = (r) => {
    generateLetterheadPDF({
      name: r.employeeName || r.requestedBy?.userName || '',
      designation: r.designation || '',
      department: r.department || r.requestedBy?.department || '',
      address: r.address || 'TSE Workspace Office, Green City, Sector-10, Lucknow - 226010',
      phone: r.phone || '+91 98765 43210',
      email: r.email || '',
      website: r.website || 'www.tseworkspace.com',
      refNo: r.refNo || r.requestId || 'TSE/LET/2025-26/XXX',
      date: r.updatedAt ? new Date(r.updatedAt).toLocaleDateString('en-IN') : new Date().toLocaleDateString('en-IN'),
      template: r.templateId?.name || 'Modern',
    });
    notify.success('PDF downloaded');
  };

  // The server decides who may act (the requester's assigned Reporting
  // Manager / HOD, their delegate, or admin) — whatever their account role is.
  const canApprove = (r) => {
    if (!user) return false;
    if (typeof r.canAct === 'boolean') return r.canAct;
    if (user.role === 'admin') return ['UNDER_RM', 'UNDER_HOD'].includes(r.status);
    if (user.role === 'manager') return r.status === 'UNDER_RM';
    if (user.role === 'hod') return r.status === 'UNDER_HOD';
    return false;
  };

  const counts = {
    ALL: requests.length,
    UNDER_RM: requests.filter(r => r.status === 'UNDER_RM').length,
    UNDER_HOD: requests.filter(r => r.status === 'UNDER_HOD').length,
    APPROVED: requests.filter(r => r.status === 'APPROVED').length,
    REJECTED: requests.filter(r => r.status === 'REJECTED').length,
  };
  const filtered = filter === 'ALL' ? requests : requests.filter(r => r.status === filter);
  const lc = useListControls(filtered, { searchFields: [r=>r.employeeName||r.requestedBy?.userName, 'requestId', 'department', 'designation'], dateField: 'createdAt', pageSize: 10 });

  return (
    <>
      <Header />
      <div id="db-wrapper"><main id="page-content">
          <Topbar section="Letterhead" /><section className="container-fluid p-4 pt-3">
        <div className="page-hero d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div><h5 className="text-white lh-1">Letterhead Approval Flow</h5><div className="sub">Sequential approval: Employee → Reporting Manager → HOD → Final Approved</div></div>
          <button onClick={load} className="btn fw-semibold" style={{background:'rgba(255,255,255,0.2)',color:'#fff',borderRadius:8,border:'1px solid rgba(255,255,255,0.3)',padding:'6px 13px'}}>
            <i className="bi bi-arrow-clockwise me-1"/>Refresh
          </button>
        </div>

        <div className="row g-2 mb-3">
          {[{label:'Pending RM',val:counts.UNDER_RM,bg:'#fffbeb',ic:'#f59e0b',icon:'bi-hourglass-split'},{label:'Pending HOD',val:counts.UNDER_HOD,bg:'#eff6ff',ic:'#3b82f6',icon:'bi-person-check'},{label:'Approved',val:counts.APPROVED,bg:'#f0fdf4',ic:'#22c55e',icon:'bi-check-circle'},{label:'Rejected',val:counts.REJECTED,bg:'#fef2f2',ic:'#ef4444',icon:'bi-x-circle'}].map((s, i) => (
            <div key={i} className="col-6 col-md-3">
              <div className="kpi-card d-flex align-items-center gap-2">
                <div className="kpi-icon" style={{background:s.bg}}><i className={`bi ${s.icon}`} style={{color:s.ic}}/></div>
                <div><div style={{fontSize:'1.17rem',fontWeight:700,lineHeight:1}}>{s.val}</div><div style={{fontSize:'0.7rem',color:'#6b7280',marginTop:2}}>{s.label}</div></div>
              </div>
            </div>
          ))}
        </div>

        <div className="d-flex gap-2 mb-3 flex-wrap">
          {[['ALL','All'],['UNDER_RM','Pending RM'],['UNDER_HOD','Pending HOD'],['APPROVED','Approved'],['REJECTED','Rejected']].map(([key, label]) => (
            <button key={key} className={`filter-chip ${filter === key ? 'active' : 'inactive'}`} onClick={() => setFilter(key)}>
              {label} <span className="chip-count">{counts[key] ?? 0}</span>
            </button>
          ))}
        </div>

        <ListToolbar search={lc.search} setSearch={lc.setSearch} dateFrom={lc.dateFrom} setDateFrom={lc.setDateFrom} dateTo={lc.dateTo} setDateTo={lc.setDateTo} placeholder="Search by name, Request ID, department..." dateLabel="Date"/>

        {loading ? <div className="text-center py-5"><span className="spinner-border text-primary"/></div>
        : lc.paginated.length === 0 ? <div className="empty-state"><i className="bi bi-inbox"/><p>No requests found</p></div>
        : lc.paginated.map((r, idx) => {
          const sc = statusConfig[r.status] || { label: r.status, cls: 'status-pending', icon: 'bi-circle' };
          const isPending = canApprove(r);
          return (
            <div key={r._id} className="record-card">
              <div className="record-number">{(lc.page-1)*lc.pageSize+idx + 1}.</div>
              <div className="record-body">
                <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                  <div style={{fontWeight:700,fontSize:'0.874rem',color:'#111827'}}>{r.employeeName || r.requestedBy?.userName}</div>
                  <span className={`status-pill ${sc.cls}`}><i className={`bi ${sc.icon}`}/>{sc.label}</span>
                  {r.templateId && <span style={{background:'#eff6ff',color:'#1d4ed8',fontSize:'0.7rem',fontWeight:600,padding:'2px 7px',borderRadius:20}}>{r.templateId.name} Template</span>}
                </div>
                <div className="row g-2">
                  <div className="col-12 col-md-5">
                    <div className="record-field"><span className="rf-label">Request ID — </span><span className="rf-value text-primary">{r.requestId}</span></div>
                    <div className="record-field"><span className="rf-label">Department — </span><span className="rf-value">{r.department || r.requestedBy?.department}</span></div>
                    <div className="record-field"><span className="rf-label">Designation — </span><span className="rf-value">{r.designation}</span></div>
                    <div className="record-field"><span className="rf-label">Date — </span><span className="rf-value">{r.date ? new Date(r.date).toLocaleDateString('en-IN') : new Date(r.createdAt).toLocaleDateString('en-IN')}</span></div>
                    {r.approvalHistory?.length > 0 && <div className="record-field"><span className="rf-label">Last Action — </span><span className="rf-value">{r.approvalHistory[r.approvalHistory.length-1]?.remarks}</span></div>}
                  </div>
                  <div className="col-12 col-md-6">
                    <div style={{fontSize:'0.7rem',color:'#9ca3af',fontWeight:600,marginBottom:4}}>APPROVAL PROGRESS</div>
                    <ApprovalTimeline status={r.status}/>
                  </div>
                </div>
              </div>
              <div className="divider d-none d-md-block"/>
              <div className="record-actions">
                {isPending && <>
                  <button className="btn-card-approve" onClick={() => approve(r)} disabled={acting === r._id}>
                    {acting === r._id ? <span className="spinner-border spinner-border-sm"/> : 'Approve'}
                  </button>
                  <div className="btn-divider"/>
                  <button className="btn-card-reject" onClick={() => reject(r)} disabled={acting === r._id}>Reject</button>
                </>}
                {r.status === 'APPROVED' && (
                  <button className="btn-card-download" onClick={() => downloadPDF(r)}>
                    <i className="bi bi-file-earmark-pdf me-1"/>PDF
                  </button>
                )}
                {r.status === 'REJECTED' && <span className="status-pill status-rejected" style={{width:100,justifyContent:'center'}}><i className="bi bi-x-circle"/>Rejected</span>}
                {!isPending && r.status !== 'APPROVED' && r.status !== 'REJECTED' && <span style={{fontSize:'0.7rem',color:'#9ca3af'}}>Awaiting approval</span>}
              </div>
            </div>
          );
        })}
        <Pagination page={lc.page} totalPages={lc.totalPages} setPage={lc.setPage} totalCount={lc.totalCount} pageSize={lc.pageSize} itemLabel="requests"/>
      </section></main></div>
    </>
  );
}
export default LetterheadApprovals;
